/**
 * Opens the PF v2 purchase session for the chosen property and stores its id in `draft.sessionId`.
 * The catalogue is read and the order placed through it, and the backend places the order on the
 * session's property.
 *
 * A session is opened whenever there is a property, a resolved workspace (the admin's target, else
 * the client's current one), for an admin the target email, and no session yet. Choosing another
 * property clears the session (`setProperty`), so the next one is opened for it; a response for a
 * property the client has since left is dropped. A resumed draft brings back its own session.
 *
 * Mounted once at the revamp shell root (`PurchaseFlowRevamp.page.tsx`).
 */
import { useEffect, useRef } from 'react'
import type { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { useAuth0 } from 'utils/auth'

import { logger } from 'utils/logging'
import { useCreatePfV2Session } from 'dataQueries/purchaseFlowV2Session.query'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useUserData } from 'components/contexts/UserDataContext'
import { usePfStore } from '../store'

interface Options {
  /** Admin-targeted user email (null/empty for client role — admin omits it). */
  targetEmail?: string | null
}

/** Narrow an unknown mutation error to its HTTP status (Axios shape). */
const errorStatus = (error: unknown): number | undefined =>
  (error as AxiosError | undefined)?.response?.status

/**
 * Establishes a PF v2 purchase session and stores the resulting `sessionId` in
 * `draftSlice`. Call once from the revamp shell root.
 *
 * @example
 * // In PurchaseFlowRevamp.page.tsx
 * useEnsurePfV2Session({ targetEmail: adminTargetEmail })
 */
export function useEnsurePfV2Session({ targetEmail = null }: Options = {}) {
  const { roles } = useAuth0()
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { userPropertyWorkspaceId } = useUserData()
  const { spawnErrorToast } = useSnackbar()
  const createSession = useCreatePfV2Session()

  const setSessionId = usePfStore((s) => s.setSessionId)
  const setSessionStatus = usePfStore((s) => s.setSessionStatus)
  const existingSessionId = usePfStore((s) => s.draft.sessionId)
  const sessionStatus = usePfStore((s) => s.draft.sessionStatus)
  const propertyId = usePfStore((s) => s.property?.id)
  // Admin-only: the workspace the admin picked for the target client.
  const adminTargetWorkspaceId = usePfStore((s) => s.targetWorkspaceId)

  // Workspace the session is opened in. Admin → picked target workspace;
  // client → its canonical property workspace. '' (unresolved) until ready.
  const workspaceId = roles.isAdmin ? adminTargetWorkspaceId : userPropertyWorkspaceId

  // The property a create is in flight for. Keyed by property, so a create for the property chosen
  // next is not blocked by one for a property the client has already left.
  const pendingForPropertyRef = useRef<string | null>(null)

  useEffect(() => {
    if (existingSessionId) return
    if (pendingForPropertyRef.current && pendingForPropertyRef.current === propertyId) return

    // After a failure, do NOT auto-retry in a render loop — wait for an explicit
    // retry (which resets status to 'idle' via `retrySession`). R1.
    if (sessionStatus === 'error') return

    // Self-gate: no property selected yet → nothing to do.
    if (!propertyId) return

    // Workspace must be resolved.
    if (!workspaceId) return

    // Admin must have a target email before the session can be opened.
    if (roles.isAdmin && !targetEmail) return

    pendingForPropertyRef.current = propertyId
    setSessionStatus('pending')
    // A response for a property no longer chosen is dropped: adopting it would place the order there.
    const isStillChosen = () => usePfStore.getState().property?.id === propertyId
    const release = () => {
      if (pendingForPropertyRef.current === propertyId) pendingForPropertyRef.current = null
    }

    createSession.mutate(
      {
        workspaceId,
        realEstatePropertyId: propertyId,
        email: roles.isAdmin ? targetEmail ?? undefined : undefined,
      },
      {
        onSuccess: (resp) => {
          // setSessionId flips sessionStatus → 'ready' for a non-null id. The
          // workspace is stamped alongside it because the v2 session-file
          // endpoints address a session by both, and the backend 403s if they
          // disagree — this is the one place both values are known together.
          release()
          if (!isStillChosen()) return
          if (resp?.sessionId) setSessionId(resp.sessionId, workspaceId, propertyId)
          else setSessionStatus('error')
        },
        onError: (error) => {
          // Mark error so the catalogue shows a retry state instead of silently
          // falling back to the mock. Release the lock so Retry can re-fire.
          release()
          if (!isStillChosen()) return

          // 422 = the property failed BE `resolveOrderableProperty` (incomplete address, null-island
          // coordinates, or a missing/zero area). The catalogue opens the property drawer to complete it.
          const status = errorStatus(error)
          const isPropertyIncomplete = status === 422
          setSessionStatus('error', isPropertyIncomplete)
          if (!isPropertyIncomplete) spawnErrorToast(t('purchase_flow_revamp:drawer.session_create_error'))

          logger.error('pf-v2 session create failed', error, {
            context: { component: 'useEnsurePfV2Session', action: 'createSession', metadata: { status } },
          })
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingSessionId, sessionStatus, propertyId, workspaceId, roles.isAdmin, targetEmail])
}
